/**
 * Merchant Banner Wizard — cross-window protocol (self-serve MVP, B2).
 *
 * Shared between the store-page overlay (editor bundle: MerchantBannerOverlay)
 * and the app-side wizard (MerchantBannerWizard). Uses its own TNX_MB_* message
 * namespace, deliberately disjoint from the consultant editor's TNX_* vocabulary
 * handled by BottomRibbon — the two flows never share a listener.
 *
 * Unlike the consultant protocol, every message here is exchanged with an
 * explicit origin on BOTH sides: the wizard passes its origin to the overlay via
 * the `tnx_app_origin` URL param, and posts to the popup with the store origin.
 */

export type BannerElementType = 'img-src' | 'bg-image' | 'img-child'

/** The merchant-confirmed control banner, as captured on the store page. */
export interface MerchantBannerControl {
  selector: string
  elementType: BannerElementType
  /** Current banner image URL (best effort — null for unresolvable bg-images). */
  imageUrl: string | null
  /** Rendered size at confirmation time (the popup's viewport ≈ desktop). */
  width: number
  height: number
  /** How many elements the selector matches (1 is the safe case). */
  matchCount: number
  isCarousel: boolean
}

export type MerchantBannerUnsupportedReason = 'carousel' | 'not_found' | 'wrong_page'

// ---------------------------------------------------------------------------
// Message types
// ---------------------------------------------------------------------------

/** Overlay (store page) → wizard (app). */
export const MB_MSG = {
  /** Overlay booted; repeated every second until the wizard ACKs. */
  READY: 'TNX_MB_READY',
  /** A candidate is highlighted, waiting for merchant confirmation. */
  CANDIDATE: 'TNX_MB_CANDIDATE',
  /** Merchant confirmed the control. Payload: { control: MerchantBannerControl } */
  CONFIRMED: 'TNX_MB_CONFIRMED',
  /** Typed refusal. Payload: { reason: MerchantBannerUnsupportedReason } */
  UNSUPPORTED: 'TNX_MB_UNSUPPORTED',
} as const

/** Wizard (app) → overlay (store page). */
export const MB_CMD = {
  /** Stops the overlay's READY announcement loop. */
  ACK: 'TNX_MB_ACK',
  /** Apply challenger preview. Payload: { previewUrl: string } */
  PREVIEW: 'TNX_MB_PREVIEW',
  /**
   * Warm challenger images in the STORE window's HTTP cache ahead of preview.
   * Payload: { urls: string[] }. Needed because Chrome partitions HTTP cache
   * by top-level site — the app window's upload thumbnails don't warm the
   * popup's cache, so an unwarmed first preview waits on a cold CDN fetch.
   */
  WARM: 'TNX_MB_WARM',
  /** Restore the original banner. */
  REVERT: 'TNX_MB_REVERT',
  /** Re-enter tap-to-select mode. */
  RESELECT: 'TNX_MB_RESELECT',
  /**
   * Re-adopt a previously confirmed control (popup was reopened mid-wizard).
   * Payload: { selector: string }. Overlay re-validates and replies CONFIRMED.
   */
  ADOPT: 'TNX_MB_ADOPT',
} as const

// ---------------------------------------------------------------------------
// Activation params (store-page side)
// ---------------------------------------------------------------------------

/** URL param that switches the editor bundle into merchant-banner mode. */
export const MB_MODE_PARAM = 'tnx_merchant_banner'
/** URL param carrying the app origin for postMessage targeting/validation. */
export const MB_APP_ORIGIN_PARAM = 'tnx_app_origin'

/**
 * Merchant mode detection with per-tab persistence: the URL param survives
 * storefront navigation only for `tnx_visual_editor` (tnx-tracking's link
 * interceptor), so we persist our flag in sessionStorage. sessionStorage is
 * per-tab, so a merchant's flag can never leak into a consultant's editor
 * popup (always a fresh window ⇒ fresh sessionStorage).
 */
export function isMerchantBannerMode(): boolean {
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get(MB_MODE_PARAM) === '1') {
      sessionStorage.setItem(MB_MODE_PARAM, '1')
      const appOrigin = params.get(MB_APP_ORIGIN_PARAM)
      if (appOrigin) sessionStorage.setItem(MB_APP_ORIGIN_PARAM, appOrigin)
      return true
    }
    return sessionStorage.getItem(MB_MODE_PARAM) === '1'
  } catch {
    return false
  }
}

/** The wizard's origin, from URL param or the per-tab persisted copy. */
export function getMerchantAppOrigin(): string | null {
  try {
    const params = new URLSearchParams(window.location.search)
    return params.get(MB_APP_ORIGIN_PARAM) || sessionStorage.getItem(MB_APP_ORIGIN_PARAM)
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Carousel policy (spec §7.2 — resolved: supported via slide pinning)
// ---------------------------------------------------------------------------

/**
 * 'first_slide': target the confirmed slide's image, pinned to its logical
 * slide index (pinnedCarouselSelector) so rotation and loop-mode clones don't
 * move the target. 'refuse' kept as an escape hatch.
 */
export const MB_CAROUSEL_POLICY: 'refuse' | 'first_slide' = 'first_slide'

// ---------------------------------------------------------------------------
// Experiment config assembly (wizard side)
// ---------------------------------------------------------------------------

export interface MbUploadedAsset {
  asset_id: string
  s3_key: string
  previewUrl: string
}

export interface MbUploads {
  mobile?: MbUploadedAsset
  desktop?: MbUploadedAsset
}

/**
 * Build the variant config for the self-serve banner experiment. Mirrors the
 * shapes TemplatesTab.buildConfig() emits for the consultant hero_banner
 * template (in_place → elements_to_swap; bg-image → hide + device-gated
 * inserts), so the backend promotion path (experiments-handler collects
 * s3_key/asset_id fields) works unchanged. Source-tagged for traceability.
 *
 * Consumed by B6 (instantiation glue); until then the wizard assembles it for
 * the recap step only.
 */
export function buildSelfServeBannerConfig(control: MerchantBannerControl, uploads: MbUploads) {
  const SOURCE = 'template:self_serve_banner'
  const urlPattern = '/' // homepage only (urlPatterns.ts TIENDANUBE_PATTERNS.homepage)

  const elements_to_hide: any[] = []
  const elements_to_insert: any[] = []
  const elements_to_swap: any[] = []

  if (control.elementType === 'img-src' || control.elementType === 'img-child') {
    // In-place: mutate the existing <img> (mobile+desktop combine into srcset
    // at runtime). Layout-preserving, no flicker.
    elements_to_swap.push({
      selector: control.selector,
      urlPattern,
      variant: 'treatment',
      asset_id_desktop: uploads.desktop?.asset_id,
      s3_key_desktop: uploads.desktop?.s3_key,
      asset_id_mobile: uploads.mobile?.asset_id,
      s3_key_mobile: uploads.mobile?.s3_key,
      source: SOURCE,
    })
  } else {
    // bg-image: hide the control block, insert the challenger image as a
    // sibling, one insert per device slot, gated to its viewport.
    elements_to_hide.push({
      selector: control.selector,
      urlPattern,
      hideIn: 'treatment' as const,
      source: SOURCE,
      description: 'Banner principal — bloque original',
    })
    for (const slot of ['mobile', 'desktop'] as const) {
      const asset = uploads[slot]
      if (!asset) continue
      elements_to_insert.push({
        selector: control.selector,
        urlPattern,
        position: 'beforebegin' as const,
        asset_id: asset.asset_id,
        s3_key: asset.s3_key,
        variant: 'treatment',
        image_type: `${slot}_image`,
        device: slot,
        source: SOURCE,
        description: `Banner principal — imagen ${slot}`,
      })
    }
  }

  return {
    elements_to_hide,
    elements_to_insert,
    elements_to_swap,
    template_config: {
      self_serve_banner: {
        selector: control.selector,
        element_type: control.elementType,
        control_image: control.imageUrl,
        control_size: { width: control.width, height: control.height },
      },
    },
  }
}
